import React, { useState, useCallback } from 'react';
import {
    Alert,
    Button,
    CopyButton,
    Group,
    Loader,
    Modal,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { Check } from '@phosphor-icons/react';
import { createConsultantInvite, lookupConsultant } from '@api/consultantInvite';
import type { ShareConsultantAccessResponse } from '@api/consultantInvite';

interface ShareModalProps {
    opened: boolean;
    onClose: () => void;
    resourceKey: string;
    merchantId: string;
    /** Hidden column keys at share time — frozen for the consultant */
    hiddenColumns?: string[];
}

type SuccessState = {
    type: 'existing_user';
    message: string;
    name: string;
} | {
    type: 'new_invite';
    link: string;
    /** Whether the backend actually mailed the link, which decides the wording below. */
    emailed: boolean;
    email: string;
};

const ShareModal: React.FC<ShareModalProps> = ({
    opened,
    onClose,
    resourceKey,
    hiddenColumns,
}) => {
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isExistingUser, setIsExistingUser] = useState(false);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<SuccessState | null>(null);

    const handleClose = () => {
        setEmail('');
        setFirstName('');
        setLastName('');
        setIsExistingUser(false);
        setLookupLoading(false);
        setLoading(false);
        setError(null);
        setSuccess(null);
        onClose();
    };

    // Lookup consultant by email when field loses focus
    const handleEmailBlur = useCallback(async () => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail || !trimmedEmail.includes('@')) return;

        setLookupLoading(true);
        setError(null);
        try {
            const result = await lookupConsultant(trimmedEmail);
            if (result.exists) {
                // Existing consultant user - pre-fill names (read-only)
                setFirstName(result.firstName ?? '');
                setLastName(result.lastName ?? '');
                setIsExistingUser(true);
            } else if (result.firstName || result.lastName) {
                // Pending invite exists - pre-fill names (editable)
                setFirstName(result.firstName ?? '');
                setLastName(result.lastName ?? '');
                setIsExistingUser(false);
            } else {
                // New user - clear names
                setFirstName('');
                setLastName('');
                setIsExistingUser(false);
            }
        } catch {
            // Silently fail lookup - user can still proceed
            setIsExistingUser(false);
        } finally {
            setLookupLoading(false);
        }
    }, [email]);

    const handleShare = async () => {
        if (!email.trim()) return;
        setLoading(true);
        setError(null);
        try {
            // Always send columnVisibility so backend can store/clear correctly
            const columnVisibility: Record<string, string[]> = {
                [resourceKey]: hiddenColumns ?? [],
            };

            const result: ShareConsultantAccessResponse = await createConsultantInvite({
                email: email.trim(),
                firstName: firstName.trim() || undefined,
                lastName: lastName.trim() || undefined,
                resourceKeys: [resourceKey],
                columnVisibility,
            });

            if (result.existingUser) {
                // Access granted to existing user
                const displayName = [result.firstName, result.lastName].filter(Boolean).join(' ') || result.email;
                setSuccess({
                    type: 'existing_user',
                    message: result.message ?? `Access granted to ${displayName}`,
                    name: displayName,
                });
            } else if (result.link) {
                // New invite created. The link is kept on screen either way — if the mail
                // did not go, it is the only way through; if it did, it is still the
                // fastest way to nudge someone over chat.
                setSuccess({
                    type: 'new_invite',
                    link: result.link,
                    emailed: result.emailed === true,
                    email: result.email,
                });
            }
        } catch {
            setError('Failed to share access. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderForm = () => (
        <>
            <Text size="sm" c="dimmed">
                {isExistingUser
                    ? 'This consultant already has an account. Click "Grant Access" to give them access to this table.'
                    : 'Invite a consultant to view this table. They will receive a link to set up their account.'
                }
            </Text>
            <TextInput
                label="Email"
                placeholder="consultant@example.com"
                value={email}
                onChange={(e) => {
                    setEmail(e.currentTarget.value);
                    // Reset existing user state when email changes
                    if (isExistingUser) {
                        setIsExistingUser(false);
                        setFirstName('');
                        setLastName('');
                    }
                }}
                onBlur={handleEmailBlur}
                required
                rightSection={lookupLoading ? <Loader size="xs" /> : null}
            />
            <Group grow>
                <TextInput
                    label="First Name"
                    placeholder={isExistingUser ? '' : 'Optional'}
                    value={firstName}
                    onChange={(e) => setFirstName(e.currentTarget.value)}
                    disabled={isExistingUser}
                    styles={isExistingUser ? { input: { backgroundColor: '#f8f9fa' } } : undefined}
                />
                <TextInput
                    label="Last Name"
                    placeholder={isExistingUser ? '' : 'Optional'}
                    value={lastName}
                    onChange={(e) => setLastName(e.currentTarget.value)}
                    disabled={isExistingUser}
                    styles={isExistingUser ? { input: { backgroundColor: '#f8f9fa' } } : undefined}
                />
            </Group>
            {error && (
                <Text size="sm" c="red">
                    {error}
                </Text>
            )}
            <Group justify="flex-end">
                <Button variant="subtle" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    onClick={handleShare}
                    loading={loading}
                    disabled={!email.trim()}
                >
                    {isExistingUser ? 'Grant Access' : 'Send Invite'}
                </Button>
            </Group>
        </>
    );

    const renderExistingUserSuccess = () => {
        if (success?.type !== 'existing_user') return null;
        return (
            <>
                <Alert icon={<Check size={20} />} color="green" variant="light">
                    <Text fw={500}>{success.message}</Text>
                    <Text size="sm" c="dimmed" mt={4}>
                        {success.name} can now access this table when they log in.
                    </Text>
                </Alert>
                <Group justify="flex-end">
                    <Button onClick={handleClose}>
                        Done
                    </Button>
                </Group>
            </>
        );
    };

    const renderNewInviteSuccess = () => {
        if (success?.type !== 'new_invite') return null;
        return (
            <>
                {success.emailed ? (
                    <Alert icon={<Check size={20} />} color="green" variant="light">
                        <Text fw={500}>Invite sent to {success.email}</Text>
                        <Text size="sm" c="dimmed" mt={4}>
                            They will get a link to set up their account, and this table will
                            be waiting for them. You can also share the link directly.
                        </Text>
                    </Alert>
                ) : (
                    <Text size="sm" c="dimmed">
                        Invite link created. Share this link with the consultant — once they set up their account, they'll automatically have access to this table.
                    </Text>
                )}
                <Group gap="xs">
                    <TextInput
                        value={success.link}
                        readOnly
                        style={{ flex: 1 }}
                    />
                    <CopyButton value={success.link}>
                        {({ copied, copy }) => (
                            <Button
                                variant="outline"
                                onClick={copy}
                                color={copied ? 'green' : undefined}
                            >
                                {copied ? 'Copied!' : 'Copy'}
                            </Button>
                        )}
                    </CopyButton>
                </Group>
                <Group justify="flex-end">
                    <Button variant="subtle" onClick={handleClose}>
                        Done
                    </Button>
                </Group>
            </>
        );
    };

    return (
        <Modal opened={opened} onClose={handleClose} title="Share Table" size="md">
            <Stack gap="md">
                {!success && renderForm()}
                {success?.type === 'existing_user' && renderExistingUserSuccess()}
                {success?.type === 'new_invite' && renderNewInviteSuccess()}
            </Stack>
        </Modal>
    );
};

export default ShareModal;
